/**
 * Resolved app environment, single source of truth.
 *
 * Derived from `VITE_SENTRY_ENVIRONMENT` (set in `.env.staging` → `staging`,
 * `.env.production` → `production`; unset on the dev server → `development`).
 * Lightweight + side-effect-free so any module (e.g. UI gating) can import it
 * without pulling in heavier modules like analytics.
 */
export type AppEnvironment = 'production' | 'staging' | 'development';

function isAppEnvironment(value: unknown): value is AppEnvironment {
  return value === 'production' || value === 'staging' || value === 'development';
}

/**
 * Resolve the environment from the raw env var, failing SAFE when it's
 * missing/invalid: a production build (`import.meta.env.PROD`, true for any
 * `vite build`) defaults to `'production'`, so a misconfigured deploy hides
 * non-prod-only UI and tags analytics as prod rather than leaking dev/staging
 * behaviour. Only the dev server (PROD === false) falls back to `'development'`.
 *
 * Note: a *valid-but-wrong* value is trusted over the build mode (Vite gives a
 * process env var priority over .env files), so the deploy build pins this
 * explicitly per target (see `scripts/render-build.sh`) — that's what stops a
 * stale inherited `VITE_SENTRY_ENVIRONMENT` from overriding the real target.
 */
export function resolveEnvironment(raw: unknown, isProdBuild: boolean): AppEnvironment {
  if (isAppEnvironment(raw)) return raw;
  return isProdBuild ? 'production' : 'development';
}

export const APP_ENVIRONMENT: AppEnvironment = resolveEnvironment(
  import.meta.env.VITE_SENTRY_ENVIRONMENT,
  import.meta.env.PROD,
);
